/**
 * Course Builder — full editor.
 *
 * Tabs:
 *   Overview   — title/code/category/summary/passing score/settings
 *   Lessons    — add/edit/delete/reorder lessons, edit lesson body, KC questions
 *   Exam Bank  — add/edit/delete/reorder exam questions, mark correct answers
 *   Publishing — draft/publish/archive controls, version history
 *
 * Route: /admin/course-builder/:courseId  (or /new for a fresh course)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../config/canonical';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Trash2, Plus, ChevronUp, ChevronDown, Loader2,
  BookOpen, ClipboardList, Settings, ShieldCheck, CheckCircle2, XCircle,
  Copy, History, Sparkles, GripVertical,
} from 'lucide-react';

const TOKEN_KEY = 'nexus_token';
const api = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Settings },
  { id: 'lessons', label: 'Lessons', icon: BookOpen },
  { id: 'exam', label: 'Exam Bank', icon: ClipboardList },
  { id: 'publishing', label: 'Publishing', icon: ShieldCheck },
];

export default function CourseBuilder() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isNew = courseId === 'new';

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (isNew) {
      setCourse({
        id: '', code: '', title: '', category: 'FOUNDATIONS', summary: '',
        estimated_hours: 4, passing_score: 80, core: false, auto_assign_on_hire: false,
        lessons: [], final_exam_pool: [], status: 'DRAFT',
      });
      return;
    }
    if (!silent) setLoading(true);
    try {
      const r = await api(`/api/training-center/admin/course-builder/courses/${courseId}`);
      setCourse(r.course);
    } catch (e) { toast.error(e.message); if (!silent) navigate('/admin/course-builder'); }
    finally { if (!silent) setLoading(false); }
  }, [courseId, isNew, navigate]);

  // Silent refresh — does NOT toggle `loading`, so the currently expanded
  // lesson editor and its local state (body/title/KCs) stay mounted after
  // a save. Fixes "content reverts/disappears after save" bug.
  const refresh = useCallback(() => load({ silent: true }), [load]);

  useEffect(() => { load(); }, [load]);

  // ------ Overview save
  const saveOverview = async () => {
    if (!course.title?.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api('/api/training-center/admin/course-builder/courses', {
          method: 'POST',
          body: JSON.stringify({
            id: course.id || undefined, code: course.code || undefined,
            title: course.title, category: course.category, summary: course.summary,
            estimated_hours: Number(course.estimated_hours) || 4,
            passing_score: Number(course.passing_score) || 80,
            core: !!course.core, auto_assign_on_hire: !!course.auto_assign_on_hire,
          }),
        });
        toast.success('Course created');
        navigate(`/admin/course-builder/${r.course.id}`, { replace: true });
      } else {
        const r = await api(`/api/training-center/admin/course-builder/courses/${course.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            title: course.title, code: course.code, category: course.category,
            summary: course.summary,
            estimated_hours: Number(course.estimated_hours) || 4,
            passing_score: Number(course.passing_score) || 80,
            core: !!course.core, auto_assign_on_hire: !!course.auto_assign_on_hire,
            image_url: course.image_url || null,
            admin_notes: course.admin_notes || null,
            instructor_notes: course.instructor_notes || null,
            randomize_questions: !!course.randomize_questions,
            randomize_answers: !!course.randomize_answers,
            exam_time_limit_min: Number(course.exam_time_limit_min) || null,
            retake_max_attempts: Number(course.retake_max_attempts) || null,
            locked: !!course.locked,
          }),
        });
        setCourse(r.course);
        toast.success('Saved');
      }
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const doAction = async (action) => {
    if (!course?.id) return;
    if (action === 'archive' && !window.confirm(`Archive "${course.title}"?`)) return;
    setSaving(true);
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/${action}`, { method: 'POST' });
      toast.success(`Course ${action}d`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const status = course.status || (course.published ? 'PUBLISHED' : 'DRAFT');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6" data-testid="course-builder">
      <div className="max-w-6xl mx-auto space-y-4">
        <header className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/course-builder')}
              data-testid="cb-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">
                {isNew ? 'New Course' : course.title}
              </h1>
              {!isNew && (
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                  <span className="font-mono">{course.id}</span>
                  <span>·</span>
                  <span>v{course.version || 1}</span>
                  <span>·</span>
                  <Badge className="text-xs">{status}</Badge>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isNew && status === 'DRAFT' && (
              <Button onClick={() => doAction('publish')}
                disabled={saving || (course.lessons || []).length === 0}
                className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold"
                data-testid="cb-publish">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Publish
              </Button>
            )}
            {!isNew && status === 'PUBLISHED' && (
              <Button variant="outline" onClick={() => doAction('unpublish')} disabled={saving}
                data-testid="cb-unpublish">
                <XCircle className="w-4 h-4 mr-1" /> Unpublish
              </Button>
            )}
          </div>
        </header>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-slate-800">
          {TABS.map(t => (
            <button key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2
                ${tab === t.id ? 'border-amber-400 text-amber-300' : 'border-transparent text-slate-400 hover:text-slate-100'}`}
              data-testid={`cb-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <OverviewTab course={course} setCourse={setCourse} isNew={isNew} saving={saving} onSave={saveOverview} />
        )}
        {tab === 'lessons' && !isNew && (
          <LessonsTab course={course} setCourse={setCourse} onChange={refresh} />
        )}
        {tab === 'exam' && !isNew && (
          <ExamBankTab course={course} setCourse={setCourse} onChange={refresh} />
        )}
        {tab === 'publishing' && !isNew && (
          <PublishingTab course={course} doAction={doAction} saving={saving} />
        )}

        {isNew && tab !== 'overview' && (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 text-slate-400 text-center">
              Save the course first (Overview tab) to unlock Lessons, Exam, and Publishing.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ------------------ OVERVIEW ------------------
function OverviewTab({ course, setCourse, isNew, saving, onSave }) {
  const setF = (k, v) => setCourse({ ...course, [k]: v });
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          {isNew && (
            <div>
              <Label>Course ID (auto if blank)</Label>
              <Input value={course.id || ''} onChange={e => setF('id', e.target.value.toUpperCase())}
                placeholder="TRC-XXX" className="bg-slate-950 border-slate-800"
                data-testid="cb-field-id" />
            </div>
          )}
          <div>
            <Label>Course Code</Label>
            <Input value={course.code || ''} onChange={e => setF('code', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-code" />
          </div>
          <div>
            <Label>Category</Label>
            <Input value={course.category || ''} onChange={e => setF('category', e.target.value.toUpperCase())}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-category" />
          </div>
          <div className="md:col-span-3">
            <Label>Title *</Label>
            <Input value={course.title || ''} onChange={e => setF('title', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-title" />
          </div>
          <div className="md:col-span-3">
            <Label>Summary / Description</Label>
            <Textarea value={course.summary || ''} onChange={e => setF('summary', e.target.value)}
              rows={3} className="bg-slate-950 border-slate-800" data-testid="cb-field-summary" />
          </div>
          <div>
            <Label>Estimated Hours</Label>
            <Input type="number" min={1} max={200} value={course.estimated_hours || 4}
              onChange={e => setF('estimated_hours', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-hours" />
          </div>
          <div>
            <Label>Passing Score (%)</Label>
            <Input type="number" min={50} max={100} value={course.passing_score || 80}
              onChange={e => setF('passing_score', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-pass" />
          </div>
          <div>
            <Label>Exam Time Limit (min)</Label>
            <Input type="number" min={5} max={240} value={course.exam_time_limit_min || 60}
              onChange={e => setF('exam_time_limit_min', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-timelimit" />
          </div>
          <div>
            <Label>Retake Max Attempts</Label>
            <Input type="number" min={1} max={20} value={course.retake_max_attempts || 3}
              onChange={e => setF('retake_max_attempts', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-retake" />
          </div>
          <div>
            <Label>Image URL</Label>
            <Input value={course.image_url || ''} onChange={e => setF('image_url', e.target.value)}
              placeholder="https://…" className="bg-slate-950 border-slate-800" data-testid="cb-field-image" />
          </div>
          <div className="md:col-span-3 grid md:grid-cols-4 gap-4">
            <ToggleRow label="Core course" checked={!!course.core} onChange={v => setF('core', v)} testid="cb-toggle-core" />
            <ToggleRow label="Auto-assign on hire" checked={!!course.auto_assign_on_hire} onChange={v => setF('auto_assign_on_hire', v)} testid="cb-toggle-auto" />
            <ToggleRow label="Randomize questions" checked={!!course.randomize_questions} onChange={v => setF('randomize_questions', v)} testid="cb-toggle-rq" />
            <ToggleRow label="Randomize answers" checked={!!course.randomize_answers} onChange={v => setF('randomize_answers', v)} testid="cb-toggle-ra" />
          </div>
          <div className="md:col-span-3">
            <Label>Admin Notes (internal only)</Label>
            <Textarea rows={3} value={course.admin_notes || ''} onChange={e => setF('admin_notes', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-admin-notes" />
          </div>
          <div className="md:col-span-3">
            <Label>Instructor Notes (shown to instructors)</Label>
            <Textarea rows={3} value={course.instructor_notes || ''} onChange={e => setF('instructor_notes', e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-field-instructor-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-800">
          <Button onClick={onSave} disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
            data-testid="cb-save-overview">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> {isNew ? 'Create Course' : 'Save Changes'}</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between p-3 bg-slate-950 rounded border border-slate-800">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// ------------------ LESSONS ------------------
function LessonsTab({ course, setCourse, onChange }) {
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const lessons = course.lessons || [];

  // Merge a freshly-saved lesson into the course state locally so the parent
  // does not have to refetch (which would collapse the open editor).
  const patchLessonLocal = (lid, patch) => {
    if (!setCourse || !lid) return;
    setCourse(prev => prev ? {
      ...prev,
      lessons: (prev.lessons || []).map(ls => ls.id === lid ? { ...ls, ...patch } : ls),
    } : prev);
  };

  const addLesson = async () => {
    if (!newLessonTitle.trim()) { toast.error('Title required'); return; }
    setBusy('add');
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/lessons`, {
        method: 'POST', body: JSON.stringify({ title: newLessonTitle.trim(), minutes: 30, body_md: '' }),
      });
      setNewLessonTitle('');
      toast.success('Lesson added');
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= lessons.length) return;
    const ids = lessons.map(l => l.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusy('reorder');
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/lessons/reorder`, {
        method: 'POST', body: JSON.stringify({ lesson_ids: ids }),
      });
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const del = async (lid) => {
    if (!window.confirm('Delete this lesson? This cannot be undone via the UI.')) return;
    setBusy(lid);
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/lessons/${lid}`, { method: 'DELETE' });
      toast.success('Lesson deleted');
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-4 space-y-3">
        <div className="flex gap-2">
          <Input value={newLessonTitle} onChange={e => setNewLessonTitle(e.target.value)}
            placeholder="New lesson title…"
            className="bg-slate-950 border-slate-800 flex-1"
            onKeyDown={e => e.key === 'Enter' && addLesson()}
            data-testid="cb-new-lesson-title" />
          <Button onClick={addLesson} disabled={busy === 'add' || !newLessonTitle.trim()}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
            data-testid="cb-add-lesson">
            <Plus className="w-4 h-4 mr-1" /> Add Lesson
          </Button>
        </div>

        {lessons.length === 0 ? (
          <div className="text-center text-slate-400 py-8">No lessons yet. Add one above to get started.</div>
        ) : (
          <div className="space-y-2">
            {lessons.map((l, idx) => (
              <div key={l.id} className="bg-slate-950 border border-slate-800 rounded" data-testid={`cb-lesson-${l.id}`}>
                <div className="flex items-center gap-2 p-3">
                  <GripVertical className="w-4 h-4 text-slate-600" />
                  <span className="text-xs text-slate-500 font-mono">#{idx + 1}</span>
                  <span className="text-xs text-slate-500 font-mono">{l.id}</span>
                  <span className="flex-1 font-medium">{l.title}</span>
                  <Badge className="text-xs bg-slate-800 border-slate-700">{l.minutes || 30}m</Badge>
                  <Badge className="text-xs bg-slate-800 border-slate-700">{(l.knowledge_check || []).length} KC</Badge>
                  <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0 || busy}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === lessons.length - 1 || busy}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                    data-testid={`cb-lesson-edit-${l.id}`}>
                    {expanded === l.id ? 'Close' : 'Edit'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => del(l.id)}
                    disabled={busy === l.id}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`cb-lesson-del-${l.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {expanded === l.id && (
                  <LessonEditor courseId={course.id} lesson={l}
                    onSavedLocal={(saved) => patchLessonLocal(l.id, saved)}
                    onSaved={onChange} />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LessonEditor({ courseId, lesson, onSaved, onSavedLocal }) {
  const [title, setTitle] = useState(lesson.title || '');
  const [minutes, setMinutes] = useState(lesson.minutes || 30);
  const [body, setBody] = useState(lesson.body || lesson.body_md || '');
  const [kc, setKc] = useState(lesson.knowledge_check || []);
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const initialRef = React.useRef(true);
  const autoTimerRef = React.useRef(null);
  const inflightRef = React.useRef(false);

  const doSave = React.useCallback(async ({ silent } = { silent: false }) => {
    if (!title.trim()) {
      if (!silent) toast.error('Lesson title required');
      return;
    }
    if (inflightRef.current) return;
    inflightRef.current = true;
    if (silent) setAutoState('saving'); else setSaving(true);
    try {
      const mins = Number(minutes) || 30;
      const r = await api(`/api/training-center/admin/course-builder/courses/${courseId}/lessons/${lesson.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title, minutes: mins, body_md: body, knowledge_check: kc }),
      });
      const saved = r?.lesson || { title, minutes: mins, body, body_md: body, knowledge_check: kc };
      if (onSavedLocal) onSavedLocal(saved);
      if (silent) {
        setAutoState('saved');
        // Fade the indicator back to idle after 2s
        setTimeout(() => setAutoState(prev => (prev === 'saved' ? 'idle' : prev)), 2000);
      } else {
        toast.success('Lesson saved');
        if (onSaved) onSaved();
      }
    } catch (e) {
      if (silent) setAutoState('error'); else toast.error(e.message);
    } finally {
      if (silent) { /* autoState set above */ } else setSaving(false);
      inflightRef.current = false;
    }
  }, [courseId, lesson.id, title, minutes, body, kc, onSaved, onSavedLocal]);

  // Debounced auto-save whenever the user changes any field. Skips the initial
  // mount so opening the editor does NOT trigger a spurious save.
  React.useEffect(() => {
    if (initialRef.current) { initialRef.current = false; return; }
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    autoTimerRef.current = setTimeout(() => { doSave({ silent: true }); }, 1800);
    return () => { if (autoTimerRef.current) clearTimeout(autoTimerRef.current); };
  }, [title, minutes, body, kc]);

  const save = () => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    doSave({ silent: false });
  };

  const addKc = () => setKc([...kc, { q: '', options: ['', '', '', ''], correct: 0, explanation: '' }]);
  const upKc = (i, patch) => setKc(kc.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const delKc = (i) => setKc(kc.filter((_, idx) => idx !== i));

  return (
    <div className="p-4 border-t border-slate-800 space-y-3 bg-slate-900/50">
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-3">
          <Label>Lesson Title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)}
            className="bg-slate-950 border-slate-800"
            data-testid={`cb-lesson-title-${lesson.id}`} />
        </div>
        <div>
          <Label>Duration (min)</Label>
          <Input type="number" value={minutes} onChange={e => setMinutes(e.target.value)}
            className="bg-slate-950 border-slate-800"
            data-testid={`cb-lesson-min-${lesson.id}`} />
        </div>
      </div>
      <div>
        <Label>Lesson Body (Markdown supported)</Label>
        <Textarea rows={10} value={body} onChange={e => setBody(e.target.value)}
          className="bg-slate-950 border-slate-800 font-mono text-sm"
          placeholder="# Overview&#10;&#10;Write the lesson body here. Supports **markdown**, ## headings, and - bullet lists."
          data-testid={`cb-lesson-body-${lesson.id}`} />
      </div>

      <div className="border-t border-slate-800 pt-3">
        <div className="flex justify-between items-center mb-2">
          <Label>Knowledge-Check Questions ({kc.length})</Label>
          <Button size="sm" variant="outline" onClick={addKc}
            data-testid={`cb-kc-add-${lesson.id}`}>
            <Plus className="w-3 h-3 mr-1" /> Add KC
          </Button>
        </div>
        <div className="space-y-3">
          {kc.map((item, i) => (
            <div key={i} className="p-3 bg-slate-950 border border-slate-800 rounded space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-mono">KC#{i + 1}</span>
                <Button size="sm" variant="ghost" onClick={() => delKc(i)}
                  className="ml-auto text-red-400"
                  data-testid={`cb-kc-del-${lesson.id}-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              <Input value={item.q || ''} onChange={e => upKc(i, { q: e.target.value })}
                placeholder="Question text…"
                className="bg-slate-900 border-slate-800"
                data-testid={`cb-kc-q-${lesson.id}-${i}`} />
              {(item.options || []).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input type="radio" checked={item.correct === oi}
                    onChange={() => upKc(i, { correct: oi })}
                    data-testid={`cb-kc-correct-${lesson.id}-${i}-${oi}`} />
                  <Input value={opt} onChange={e => {
                    const options = [...item.options]; options[oi] = e.target.value;
                    upKc(i, { options });
                  }} placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                    className="bg-slate-900 border-slate-800 flex-1" />
                </div>
              ))}
              <Textarea rows={2} value={item.explanation || ''} onChange={e => upKc(i, { explanation: e.target.value })}
                placeholder="Explanation shown after answering…"
                className="bg-slate-900 border-slate-800 text-sm" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs" data-testid={`cb-lesson-autosave-${lesson.id}`}>
          {autoState === 'saving' && (
            <span className="text-slate-400 inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Auto-saving…
            </span>
          )}
          {autoState === 'saved' && (
            <span className="text-emerald-400 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Draft saved
            </span>
          )}
          {autoState === 'error' && (
            <span className="text-red-400 inline-flex items-center gap-1.5">
              <XCircle className="w-3 h-3" /> Auto-save failed · use Save Lesson
            </span>
          )}
          {autoState === 'idle' && (
            <span className="text-slate-500">Edits auto-save after ~2s pause</span>
          )}
        </div>
        <Button onClick={save} disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
          data-testid={`cb-lesson-save-${lesson.id}`}>
          {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save Lesson</>}
        </Button>
      </div>
    </div>
  );
}

// ------------------ EXAM BANK ------------------
function ExamBankTab({ course, setCourse, onChange }) {
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const pool = course.final_exam_pool || [];

  const addBlank = async () => {
    setBusy('add');
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/exam/questions`, {
        method: 'POST',
        body: JSON.stringify({
          q: 'New question — click Edit to fill in',
          options: ['Option A', 'Option B', 'Option C', 'Option D'],
          correct: 0, explanation: '', points: 1, type: 'multiple_choice',
        }),
      });
      toast.success('Question added');
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const del = async (i) => {
    if (!window.confirm('Delete this question?')) return;
    setBusy(i);
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/exam/questions/${i}`, { method: 'DELETE' });
      toast.success('Question deleted');
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const move = async (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= pool.length) return;
    const indices = pool.map((_, idx) => idx);
    [indices[i], indices[t]] = [indices[t], indices[i]];
    setBusy('reorder');
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/exam/questions/reorder`, {
        method: 'POST', body: JSON.stringify({ indices }),
      });
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[200px] text-sm text-slate-400">
            Exam bank: <b className="text-slate-100">{pool.length}</b> questions · Pass ≥ {course.passing_score}%
          </div>
          <Button onClick={() => setGenOpen(true)}
            className="bg-purple-500 hover:bg-purple-400 text-white"
            data-testid="cb-exam-ai">
            <Sparkles className="w-4 h-4 mr-1" /> AI-Generate Questions
          </Button>
          <Button onClick={addBlank} disabled={busy === 'add'}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
            data-testid="cb-exam-add">
            <Plus className="w-4 h-4 mr-1" /> Add Question
          </Button>
        </div>

        {pool.length === 0 ? (
          <div className="text-center text-slate-400 py-8">No questions yet. Add one manually or use AI-Generate.</div>
        ) : (
          <div className="space-y-2">
            {pool.map((q, i) => (
              <div key={i} className="bg-slate-950 border border-slate-800 rounded" data-testid={`cb-q-row-${i}`}>
                <div className="flex items-center gap-2 p-3">
                  <span className="text-xs text-slate-500 font-mono w-8">Q{i + 1}</span>
                  <span className="flex-1 text-sm">{(q.q || '').slice(0, 90)}{(q.q || '').length > 90 ? '…' : ''}</span>
                  <Badge className="text-xs bg-slate-800 border-slate-700">{(q.options || []).length} opts</Badge>
                  <Badge className="text-xs bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                    ✓ {String.fromCharCode(65 + (q.correct || 0))}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0 || busy}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === pool.length - 1 || busy}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setExpanded(expanded === i ? null : i)}
                    data-testid={`cb-q-edit-${i}`}>
                    {expanded === i ? 'Close' : 'Edit'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => del(i)}
                    className="text-red-400"
                    data-testid={`cb-q-del-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {expanded === i && (
                  <QuestionEditor courseId={course.id} index={i} question={q} onSaved={() => { setExpanded(null); onChange(); }} />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {genOpen && <ExamAiDialog courseId={course.id} onClose={() => setGenOpen(false)} onDone={() => { setGenOpen(false); onChange(); }} />}
    </Card>
  );
}

function QuestionEditor({ courseId, index, question, onSaved }) {
  const [q, setQ] = useState(question.q || '');
  const [options, setOptions] = useState(question.options || ['', '', '', '']);
  const [correct, setCorrect] = useState(question.correct || 0);
  const [explanation, setExplanation] = useState(question.explanation || '');
  const [type, setType] = useState(question.type || 'multiple_choice');
  const [points, setPoints] = useState(question.points || 1);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!q.trim()) { toast.error('Question text required'); return; }
    if (!options.every(o => o?.trim())) { toast.error('All options must have text'); return; }
    setSaving(true);
    try {
      await api(`/api/training-center/admin/course-builder/courses/${courseId}/exam/questions/${index}`, {
        method: 'PATCH',
        body: JSON.stringify({ q, options, correct: Number(correct), explanation, type, points: Number(points) }),
      });
      toast.success('Question saved');
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const addOpt = () => setOptions([...options, '']);
  const delOpt = (i) => {
    if (options.length <= 2) { toast.error('Minimum 2 options'); return; }
    const next = options.filter((_, idx) => idx !== i);
    setOptions(next);
    if (correct >= next.length) setCorrect(0);
  };

  return (
    <div className="p-4 border-t border-slate-800 space-y-3 bg-slate-900/50">
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-3">
          <Label>Question Type</Label>
          <select value={type} onChange={e => setType(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-100"
            data-testid={`cb-q-type-${index}`}>
            <option value="multiple_choice">Multiple Choice</option>
            <option value="true_false">True/False</option>
            <option value="short_answer">Short Answer</option>
            <option value="scenario">Scenario-Based</option>
            <option value="essay">Essay</option>
          </select>
        </div>
        <div>
          <Label>Points</Label>
          <Input type="number" min={1} max={100} value={points} onChange={e => setPoints(e.target.value)}
            className="bg-slate-950 border-slate-800"
            data-testid={`cb-q-points-${index}`} />
        </div>
      </div>
      <div>
        <Label>Question Text *</Label>
        <Textarea rows={3} value={q} onChange={e => setQ(e.target.value)}
          className="bg-slate-950 border-slate-800"
          data-testid={`cb-q-text-${index}`} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Answer Options (mark ✓ for correct)</Label>
          <Button size="sm" variant="outline" onClick={addOpt}
            data-testid={`cb-q-add-opt-${index}`}>
            <Plus className="w-3 h-3 mr-1" /> Add option
          </Button>
        </div>
        <div className="space-y-2">
          {options.map((opt, oi) => (
            <div key={oi} className="flex items-center gap-2">
              <input type="radio" checked={correct === oi} onChange={() => setCorrect(oi)}
                data-testid={`cb-q-correct-${index}-${oi}`} />
              <span className="text-xs text-slate-500 w-6">{String.fromCharCode(65 + oi)}.</span>
              <Input value={opt} onChange={e => { const n = [...options]; n[oi] = e.target.value; setOptions(n); }}
                className="bg-slate-950 border-slate-800 flex-1" />
              <Button size="sm" variant="ghost" onClick={() => delOpt(oi)}
                className="text-red-400" disabled={options.length <= 2}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <Label>Explanation (shown after grading)</Label>
        <Textarea rows={2} value={explanation} onChange={e => setExplanation(e.target.value)}
          className="bg-slate-950 border-slate-800 text-sm"
          data-testid={`cb-q-expl-${index}`} />
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
          data-testid={`cb-q-save-${index}`}>
          {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save Question</>}
        </Button>
      </div>
    </div>
  );
}

function ExamAiDialog({ courseId, onClose, onDone }) {
  const [n, setN] = useState(20);
  const [overwrite, setOverwrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api(`/api/training-center/admin/courses/${courseId}/generate-exam-bank`, {
        method: 'POST',
        body: JSON.stringify({ target_n: Number(n) || 20, overwrite }),
      });
      toast.success(`Generated ${r.questions_generated} questions. Pool now ${r.pool_size}.`);
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}
         data-testid="cb-exam-ai-dialog">
      <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" /> AI-Generate Exam Questions
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          GPT-5 will generate additional exam questions grounded in the course&apos;s lesson content.
        </p>
        <div className="space-y-3">
          <div>
            <Label>Number to generate</Label>
            <Input type="number" min={1} max={100} value={n} onChange={e => setN(e.target.value)}
              className="bg-slate-950 border-slate-800" data-testid="cb-exam-ai-n" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)}
              data-testid="cb-exam-ai-overwrite" />
            Replace existing pool (default: append)
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            className="bg-purple-500 hover:bg-purple-400 text-white"
            data-testid="cb-exam-ai-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating…</> : 'Generate'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ------------------ PUBLISHING ------------------
function PublishingTab({ course, doAction, saving }) {
  const [versions, setVersions] = useState([]);
  const [loadingV, setLoadingV] = useState(false);

  const loadV = useCallback(async () => {
    setLoadingV(true);
    try {
      const r = await api(`/api/training-center/admin/course-builder/courses/${course.id}/versions`);
      setVersions(r.versions || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingV(false); }
  }, [course.id]);

  useEffect(() => { loadV(); }, [loadV]);

  const restore = async (vid) => {
    if (!window.confirm('Restore this version? Current course state will be snapshotted first.')) return;
    try {
      await api(`/api/training-center/admin/course-builder/courses/${course.id}/restore/${vid}`, { method: 'POST' });
      toast.success('Restored — reload the page to see changes');
      window.location.reload();
    } catch (e) { toast.error(e.message); }
  };

  const status = course.status || (course.published ? 'PUBLISHED' : 'DRAFT');

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-6 space-y-6">
        <section>
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-amber-400" /> Publishing Status
          </h3>
          <div className="grid md:grid-cols-3 gap-3">
            <StatusCard label="Current status" value={status} big />
            <StatusCard label="Version" value={`v${course.version || 1}`} big />
            <StatusCard label="Last edit" value={course.last_edited_by_name || '—'} />
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {status === 'DRAFT' && (
              <Button onClick={() => doAction('publish')} disabled={saving || (course.lessons || []).length === 0}
                className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold"
                data-testid="cb-pub-publish">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Publish Course
              </Button>
            )}
            {status === 'PUBLISHED' && (
              <Button onClick={() => doAction('unpublish')} disabled={saving} variant="outline"
                data-testid="cb-pub-unpublish">
                <XCircle className="w-4 h-4 mr-1" /> Unpublish
              </Button>
            )}
            {status !== 'ARCHIVED' && (
              <Button onClick={() => doAction('archive')} disabled={saving} variant="outline"
                className="text-red-400 hover:bg-red-500/10"
                data-testid="cb-pub-archive">
                Archive
              </Button>
            )}
            <Button onClick={() => doAction('duplicate')} disabled={saving} variant="outline"
                data-testid="cb-pub-duplicate">
                <Copy className="w-4 h-4 mr-1" /> Duplicate
            </Button>
          </div>
        </section>

        <section>
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <History className="w-5 h-5 text-amber-400" /> Version History
          </h3>
          {loadingV ? (
            <div className="text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-slate-400">No previous snapshots yet. Snapshots are captured on publish/unpublish/archive.</div>
          ) : (
            <div className="space-y-1">
              {versions.map(v => (
                <div key={v.id} className="flex items-center gap-3 p-2 bg-slate-950 rounded border border-slate-800"
                     data-testid={`cb-version-${v.id}`}>
                  <span className="text-xs font-mono text-slate-500">{v.id}</span>
                  <span className="text-xs">v{v.version}</span>
                  <Badge className="text-xs bg-slate-800 border-slate-700">{v.reason}</Badge>
                  <span className="text-xs text-slate-400">{new Date(v.created_at).toLocaleString()}</span>
                  <Button size="sm" variant="outline" className="ml-auto" onClick={() => restore(v.id)}
                    data-testid={`cb-restore-${v.id}`}>
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function StatusCard({ label, value, big }) {
  return (
    <div className="p-3 bg-slate-950 border border-slate-800 rounded">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`mt-1 font-semibold ${big ? 'text-lg' : ''}`}>{value}</div>
    </div>
  );
}
